import * as React from "react";
import { locKeys } from "components/flexComponents/WizardFlightPWA/components/l10n";
import { MobXProviderContext } from "mobx-react";
import { PositionType } from "uitk-react-menu";
import { useLocalization } from "@shared-ui/localization-context";
import { FlightType } from "src/stores/wizard/state/typings";
import { UitkExperimentalAdaptive } from "uitk-react-experimental-adaptive";
import { QueryRoute } from "bernie-core";
import { Layer, Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { ToolbarType, UitkToolbarSecondaryAction } from "uitk-react-toolbar";
import { UitkRadioGroup, UitkRadio } from "uitk-react-switch";
import { UitkText } from "uitk-react-text";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkPrimaryButton, UitkSecondaryButton, UitkButtonSize } from "uitk-react-button";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";

export interface FlightTypeAdaptiveMenuProps {
  /**
   * @description UI element that when clicked will display adaptive menu
   * @example:
   * <UitkLink iconPos={UitkLinkAlign.RIGHT}>
   *  <a href="" role="button">
   *    Display adaptive flight type menu
   *    <UitkIcon name="expand_more" size={UitkIconSize.SMALL} />
   *  </a>
   * </UitkLink>
   */
  trigger: JSX.Element;
  /** Initial selected value in radio group */
  initialFlightType: string;
  /** Confirm selection of flight type */
  onSelectFlightType: (flightType: string) => void;
  /** Perform custom action when clear button is clicked */
  onClearButtonClick: () => void;
  /** Display one way flight type */
  showOneway: boolean;
  /** Display multicity flight type */
  showMulticity: boolean;
}

export type flightTypeRadioItem = {
  text: string;
  type: string;
};

export const FlightTypeAdaptiveMenu = ({
  showOneway,
  showMulticity,
  trigger,
  onSelectFlightType,
  initialFlightType,
  onClearButtonClick,
}: FlightTypeAdaptiveMenuProps) => {
  const { formatText } = useLocalization();
  const prefix = "flightTypeAdaptiveMenu";
  const queryRouteId = `${prefix}Dialog`;
  const { routeStore } = React.useContext(MobXProviderContext);
  const [displayMenu, setDisplayMenu] = React.useState(false);
  const menuPosition = PositionType.LEFT;

  const flightTypeMap = {
    [FlightType.ROUND_TRIP]: { text: locKeys.flightRoundTripText, type: FlightType.ROUND_TRIP },
  };

  if (showOneway === true) {
    flightTypeMap[FlightType.ONE_WAY] = { text: locKeys.flightOneWayText, type: FlightType.ONE_WAY };
  }

  if (showMulticity === true) {
    flightTypeMap[FlightType.MULTI_CITY] = { text: locKeys.flightMultiCityText, type: FlightType.MULTI_CITY };
  }

  const initialFlightTypeKey =
    Object.keys(flightTypeMap).find((key) => formatText(flightTypeMap[key].text) === initialFlightType) ||
    FlightType.ROUND_TRIP;
  const [selectedFlightType, setSelectedFlightType] = React.useState<FlightType>(
    flightTypeMap[initialFlightTypeKey].type
  );

  const getDialogWrapper = (children: JSX.Element) => (
    <QueryRoute query={{ pwaDialog: queryRouteId }}>
      <Layer id={queryRouteId}>{children}</Layer>
    </QueryRoute>
  );

  /**
   * Display dialog
   */
  const handleDialogTriggerClick = () => {
    routeStore.updateSearch({ pwaDialog: queryRouteId });
    setDisplayMenu((prevState) => !prevState);
  };

  /**
   * Display/hide adaptive menu content when trigger is clicked
   * Small viewport size (mobile) is not currently supported by MIT_PWA_PKGFLEXIBILITY_ONEWAYPKG, MIT_PWA_PKGFLEXIBILITY_MCITYPKG
   * which are the experiments that evaluate showOneway, showMulticity props.
   * This function needed for the <ExperimentalAdaptive /> but not unavailable for users
   */
  /* istanbul ignore next */
  const handleMenuTriggerClick = () => {
    setDisplayMenu((prevState) => !prevState);
  };

  /**
   * Hide dialog on mobile and adaptive menu on medium and bigger viewport sizes
   * Small viewport size (mobile) is not currently supported by MIT_PWA_PKGFLEXIBILITY_ONEWAYPKG, MIT_PWA_PKGFLEXIBILITY_MCITYPKG
   * which are the experiments that evaluate showOneway, showMulticity props.
   * This function needed for the <ExperimentalAdaptive /> but not unavailable for users
   */
  /* istanbul ignore next */
  const onDismissDialog = () => {
    routeStore.goBack();
    setDisplayMenu(false);
  };

  /**
   * @return trigger element to be used inside adaptive menu
   */
  const renderAdaptiveTrigger = () => trigger;

  /** Select a flight when done button is clicked */
  const onDoneButtonClick = () => {
    onSelectFlightType(selectedFlightType);
    setDisplayMenu(false);
    routeStore.updateSearch({ pwaDialog: "" });
  };

  /** Hide adaptive menu/dialog */
  const handleClearButtonClick = () => {
    setDisplayMenu(false);
    routeStore.updateSearch({ pwaDialog: "" });
    onClearButtonClick();
  };

  /** Select flight type from radio group (scope is restricted to this component) */
  const handleFlightTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFlightType(e.target.value as FlightType);
  };

  return (
    <div id="flight-type-adaptive-menu">
      <UitkExperimentalAdaptive
        showAdaptive={displayMenu}
        dialogWrapper={getDialogWrapper}
        handleDialogClick={handleDialogTriggerClick}
        handleMenuClick={handleMenuTriggerClick}
        adaptiveTrigger={renderAdaptiveTrigger}
        menuPosition={menuPosition}
        toolbar={{
          iconLabel: formatText(locKeys.flightTypeDialogDismissLabel),
          header: formatText(locKeys.flightTypeDialogTitle),
          type: ToolbarType.CLOSE,
          action: onDismissDialog,
          actionContent: (
            <UitkToolbarSecondaryAction
              actionText={formatText(locKeys.flightTypeDialogClearButton)}
              onClick={handleClearButtonClick}
            />
          ),
        }}
      >
        <>
          <section className="all-t-padding-five all-b-padding-twelve">
            {/*
                Display title only on medium and bigger viewport sizes to avoid redundancy in small view
                due to Toolbar title is already displaying this string
              */}
            <Viewport>
              <ViewSmall />
              <ViewMedium>
                <div className="all-b-padding-four">
                  <UitkText size={400} weight="bold">
                    {formatText(locKeys.flightTypeDialogTitle)}
                  </UitkText>
                </div>
              </ViewMedium>
            </Viewport>

            <div className="all-b-padding-twelve">
              <UitkRadioGroup group={`${prefix}-group`} onChange={handleFlightTypeChange} selected={selectedFlightType}>
                {Object.keys(flightTypeMap).map((key) => {
                  const { text, type } = flightTypeMap[key];

                  return (
                    <UitkRadio
                      key={`cruise-destination-radio-${type}`}
                      id={`cruise-destination-radio-${type}`}
                      value={type}
                    >
                      {formatText(text)}
                    </UitkRadio>
                  );
                })}
              </UitkRadioGroup>
            </div>
          </section>

          <UitkScrim className={`${prefix}Actions`} type={UitkScrimType.BUTTON}>
            <Viewport>
              <ViewSmall>
                <UitkPrimaryButton
                  onClick={onDoneButtonClick}
                  text={formatText(locKeys.flightTypeDialogDoneButton)}
                  size={UitkButtonSize.LARGE}
                  isFullWidth
                />
              </ViewSmall>

              <ViewMedium>
                <UitkLayoutGrid columns={2} space="three">
                  <UitkLayoutGridItem>
                    <div>
                      <UitkSecondaryButton
                        onClick={handleClearButtonClick}
                        text={formatText(locKeys.flightTypeDialogClearButton)}
                        size={UitkButtonSize.SMALL}
                        isFullWidth
                      />
                    </div>
                  </UitkLayoutGridItem>

                  <UitkLayoutGridItem>
                    <div>
                      <UitkPrimaryButton
                        onClick={onDoneButtonClick}
                        text={formatText(locKeys.flightTypeDialogDoneButton)}
                        size={UitkButtonSize.SMALL}
                        isFullWidth
                      />
                    </div>
                  </UitkLayoutGridItem>
                </UitkLayoutGrid>
              </ViewMedium>
            </Viewport>
          </UitkScrim>
        </>
      </UitkExperimentalAdaptive>
    </div>
  );
};
